/* eslint-disable max-len */
import { Trans, t } from '@lingui/macro';
import React from 'react';
import { Helmet } from 'react-helmet-async';

import AndroidBanner from 'Components/AndroidBanner/AndroidBanner';
import PageView from 'common/PageView/PageView';
import config from 'config/config';
import { Heading1, Heading2, Heading3 } from 'uikit/Heading';
import { TextP1, TextP3, TextDisplay } from 'uikit/Text';
import routes from 'utils/routeTranslator';

import './AboutUs.scss';
import moneyLogo from './img/money.png';
import naszemiastoLogo from './img/naszemiasto.png';
import natematLogo from './img/natemat.svg';
import pracaLogo from './img/praca.png';
import wirtualnemediaLogo from './img/wirtualnemedia.png';
import wprostLogo from './img/wprost.png';

type MediaItem = {
  logo: string;
  width: string;
  height: string;
  title: string;
  url: string;
  alt: string;
};

const media: MediaItem[] = [
  {
    logo: natematLogo,
    width: '150',
    height: '45',
    title: 'Szukasz sprawdzonego wykonawcy? Bądź sprytny i spraw, by to on cię znalazł!',
    url: 'https://natemat.pl/309107,fixly-dla-klientow-aplikcja-ktora-pomoze-znalezc-sprawdzonego-specjaliste ',
    alt: 'natemat.pl',
  },
  {
    logo: wprostLogo,
    width: '156',
    height: '50',
    title: 'Jak znaleźć sprawdzonego fachowca?',
    url: 'https://www.wprost.pl/zycie/10224402/jak-znalezc-sprawdzonego-fachowca.html',
    alt: 'wprost.pl',
  },
  {
    logo: moneyLogo,
    width: '177',
    height: '50',
    title: 'Fixly - startup, który znajdzie dla Ciebie fachowca',
    url: 'https://www.money.pl/gospodarka/fixly-startup-ktory-znajdzie-dla-ciebie-fachowca-6416592190892161a.html',
    alt: 'money.pl',
  },
  {
    logo: wirtualnemediaLogo,
    width: '234',
    height: '28',
    title: 'Platforma Fixly zachęca w reklamie "Nie kupuj - naprawiaj"',
    url: 'https://www.wirtualnemedia.pl/artykul/fixly-nowa-kategoria-i-reklama-nie-kupuj-naprawiaj',
    alt: 'wirtualnemedia.pl',
  },
  {
    logo: pracaLogo,
    width: '200',
    height: '50',
    title: 'Fixly.pl – serwis ze zleceniami dla wykonawców usług',
    url: 'https://pracabezszefa.pl/fixly-pl-serwis-ze-zleceniami-dla-wykonawcow-uslug',
    alt: 'pracabezszefa.pl',
  },
  {
    logo: naszemiastoLogo,
    width: '234',
    height: '123',
    title: 'Jak w 5 minut zlecić usługę i znaleźć fachowca?',
    url: 'https://naszemiasto.pl/jak-w-5-minut-zlecic-usluge-i-znalezc-fachowca/ar/c15-8191413',
    alt: 'naszemiasto.pl',
  },
];

const AboutUs = () => {
  const titleSegments = [t`O nas`, t`Pomagamy użytkownikom znaleźć fachowców`, 'Fixly.pl'];
  const siteTitle = titleSegments.join(' - ');
  const ogTitle = titleSegments.join(' | ');
  const ogDescription = t`Fixly - innowacyjny serwis od OLX. Łączymy zlecających z wykonawcami usług. Dbamy, aby znalezienie fachowca, który pomoże rozwiązać dany problem było proste!`;
  const siteDescription = t`Jesteś blogerem lub vlogerem? Prowadzisz serwis internetowy o tematyce budowlano-remontowej lub szukasz dodatkowego źródła zarobku? Zostań partnerem Fixly!`;

  return (
    <div className="aboutUs">
      <Helmet>
        <title>{siteTitle}</title>
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta name="description" content={siteDescription} />
        <link rel="canonical" href={config.SITE_URL + routes.get('front::aboutus')} />
      </Helmet>
      <PageView pageLabel="aboutus_page" />

      <AndroidBanner />

      <section className="aboutUs__section">
        <Heading1 className="aboutUs__title">
          <Trans>O Fixly</Trans>
        </Heading1>
        <TextP1 className="aboutUs__paragraph">
          <Trans>
            Fixly to serwis, który łączy osoby zlecające usługę z odpowiednimi wykonawcami. Remont mieszkania, meble na
            wymiar, transport, pomoc prawna, naprawa samochodu, a może organizacja imprezy? Te i wiele więcej usług
            znajdziesz na Fixly. Wystarczy dodać zapytanie na stronie bądź w wygodnej aplikacji mobilnej, aby otrzymać
            oferty od specjalistów z okolicy.
          </Trans>
        </TextP1>
      </section>

      <section className="aboutUs__section">
        <Heading2 className="aboutUs__title2">
          <Trans>Jak działa Fixly?</Trans>
        </Heading2>
        <div className="aboutUs__scalerWrap">
          <div className="aboutUs__scaler">
            <iframe
              title="about us youtube"
              className="aboutUs__video"
              id="promoVideo-container"
              frameBorder={0}
              src="https://www.youtube.com/embed/Pz1efcpHKtE?showinfo=0&mute=1&autoplay=1&enablejsapi=1&origin=*&rel=0"
              allow="autoplay"
              allowFullScreen
            />
          </div>
        </div>
        <TextP1 className="aboutUs__paragraph">
          <Trans>
            Fixly proponuje odwrócony model serwisu ogłoszeniowego. Oznacza to, że zlecający informuje, jaką usługę ma
            do wykonania, a specjaliści sami kontaktują się w sprawie jej realizacji. Wystarczy wybrać jedną z
            dostępnych kategorii, wskazać lokalizację i odpowiedzieć na kilka pytań dotyczących szczegółów zlecenia.
            Zainteresowani wykonawcy skontaktują się ze zlecającym, wysyłając swoje oferty. Cały proces trwa tylko kilka
            minut!
          </Trans>
        </TextP1>
      </section>
      <section className="aboutUs__stats">
        <div className="aboutUs__stat">
          <Heading3 className="aboutUs__statTitle">400+</Heading3>
          <TextDisplay>
            <Trans>dostępnych usług</Trans>
          </TextDisplay>
        </div>
        <div className="aboutUs__stat">
          <Heading3 className="aboutUs__statTitle">160 000+</Heading3>
          <TextDisplay>
            <Trans>zarejestrowanych wykonawców</Trans>
          </TextDisplay>
        </div>
        <div className="aboutUs__stat">
          <Heading3 className="aboutUs__statTitle">
            4.7
            <span className="aboutUs__statIcon"></span>
          </Heading3>
          <TextDisplay>
            <Trans>średnia ocena wszystkich wykonawców</Trans>
          </TextDisplay>
        </div>
        <div className="aboutUs__stat">
          <Heading3 className="aboutUs__statTitle">
            <Trans>4 minuty</Trans>
          </Heading3>
          <TextDisplay>
            <Trans>średni czas oczekiwania na pierwsze zgłoszenie</Trans>
          </TextDisplay>
        </div>
      </section>

      <section className="aboutUs__section">
        <Heading2 className="aboutUs__title2">
          <Trans>Dlaczego Fixly?</Trans>
        </Heading2>
        <TextP1 className="aboutUs__paragraph">
          <Trans>
            Fixly umożliwia wygodny i szybki sposób na zlecanie usług. Wystarczy dodać zapytanie, a resztę wykona Fixly,
            łącząc zlecającego z odpowiednimi wykonawcami. W kilka minut można zebrać oferty od zainteresowanych
            specjalistów z okolicy, oszczędzając czas na samodzielnym szukaniu.
          </Trans>
        </TextP1>
        <TextP1 className="aboutUs__paragraph">
          <Trans>
            Wiemy, jak ważne w branży usługowej są polecenia, dlatego istotną rolę na Fixly odgrywają opinie. Dlatego
            przed wyborem wykonawcy można przejrzeć oceny, które wystawili wcześniejsi klienci, sprawdzając tym samym,
            czy dany profesjonalista jest polecany przez innych. Z kolei po realizacji zlecenia można samemu ocenić
            danego fachowca, dając znać kolejnym klientom, czy warto korzystać z jego usług.
          </Trans>
        </TextP1>
        <div className="aboutUs__reviewsLink">
          <a href={routes.get('front::reviews')}>
            <Trans>Sprawdź opinie o wykonawcach na Fixly</Trans>
          </a>
        </div>

        <TextP1 className="aboutUs__paragraph">
          <Trans>
            Sprawdź sam i wypróbuj Fixly! Jeśli potrzebujesz pomocy przy dodawaniu swojego pierwszego zapytania, to
            zajrzyj do{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://pomoc.fixly.pl/hc/pl/articles/360004811060-Rejestracja-i-wybieranie-wykonawcy"
            >
              tego artykułu.
            </a>
          </Trans>
        </TextP1>

        <TextP1 className="aboutUs__paragraph">
          <Trans>
            Jesteś wykonawcą? Zainteresowało Cię działanie serwisu? Załóż bezpłatnie konto wykonawcy i zacznij zdobywać
            nowych klientów z Fixly!
          </Trans>{' '}
          <a href={routes.get('front::provider.register')}>
            <Trans>Zarejestruj się tutaj.</Trans>
          </a>
        </TextP1>
      </section>

      <section className="aboutUs__section">
        <Heading2 className="aboutUs__title2">
          <Trans>Media o nas</Trans>
        </Heading2>
        <div className="aboutUs__mediaList">
          {media.map((m) => (
            <div className="aboutUs__media" key={m.title}>
              <div className="aboutUs__mediaLogoWrap">
                <img src={m.logo} className="aboutUs__mediaLogo" width={m.width} height={m.height} alt={m.alt} />
              </div>
              <TextP1 bold className="aboutUs__mediaTitle">
                {m.title}
              </TextP1>
              <TextP3 bold>
                <a target="_blank" rel="noopener noreferrer" href={m.url}>
                  <Trans>Pokaż więcej</Trans>
                </a>
              </TextP3>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
